import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { useClaimants } from '@/hooks/cmm/useClaimants';

// ── Badge color maps ─────────────────────────────────────────
const TORT_COLORS: Record<string, string> = {
  VGA:  'bg-blue-100 text-blue-800',
  SMA:  'bg-purple-100 text-purple-800',
  RLP:  'bg-green-100 text-green-800',
  PFAS: 'bg-amber-100 text-amber-800',
};
const STAGE_COLORS: Record<string, string> = {
  INTAKE:         'bg-gray-100 text-gray-700',
  DOC_COLLECTION: 'bg-blue-100 text-blue-700',
  REVIEW:         'bg-yellow-100 text-yellow-700',
  SUBSTANTIATED:  'bg-green-100 text-green-700',
  DEFICIENT:      'bg-red-100 text-red-700',
  AWARD_READY:    'bg-emerald-100 text-emerald-700',
  PRE_LITIGATION: 'bg-purple-100 text-purple-700',
};

function tortColor(code: string): string {
  return TORT_COLORS[code] ?? 'bg-gray-100 text-gray-800';
}
function stageColor(stage: string): string {
  return STAGE_COLORS[stage] ?? 'bg-gray-100 text-gray-700';
}
function dpvColor(code: string): string {
  if (code === 'Y') return 'bg-green-100 text-green-800';
  if (code === 'S') return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
}
function dpvLabel(code: string): string {
  if (code === 'Y') return 'Validated';
  if (code === 'S') return 'Secondary';
  return 'Unvalidated';
}

// ── Formatting helpers ────────────────────────────────────────
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}
function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

// ── Reusable layout primitives ────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4 pb-2 border-b border-gray-100">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
      <div className="text-sm text-gray-900">{children}</div>
    </div>
  );
}

function StubButton({ label, title }: { label: string; title: string }) {
  return (
    // TODO: wire each stub to its respective API endpoint — see Section comments below
    <button
      disabled
      title={title}
      className="px-3 py-1.5 text-sm border border-dashed border-gray-300 rounded-md text-gray-400 cursor-not-allowed select-none"
    >
      {label}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────
export default function ClaimantDetailCard() {
  const { id } = useParams<{ id: string }>();

  // Load all claimants (no tort filter) and find by id.
  // TODO: replace with a dedicated GET /claimants/:id endpoint once available.
  const { claimants, isLoading } = useClaimants();
  const claimant = claimants.find(c => c.id === id);

  // ── Loading state ─────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="text-gray-400 text-sm">Loading…</span>
      </div>
    );
  }

  // ── Not found state ───────────────────────────────────────
  if (!claimant) {
    return (
      <div className="p-6">
        <p className="text-gray-500 text-sm mb-4">Claimant not found.</p>
        <Link
          to="/claimants/spreadsheet"
          className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Spreadsheet View
        </Link>
      </div>
    );
  }

  const maskedSsn = `***-**-${claimant.ssnLast4}`;
  const overlapIds = claimant.crossLitigationMatchIds ?? [];

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 mb-3">
        <span>Home</span><span className="mx-2">›</span>
        <span>Claimants</span><span className="mx-2">›</span>
        <span className="text-gray-900 font-medium">
          {claimant.firstName} {claimant.lastName}
        </span>
      </nav>

      {/* Back link — navigates within the tab, does not close it */}
      <Link
        to="/claimants/spreadsheet"
        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 mb-5"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Spreadsheet View
      </Link>

      {/* ── HEADER (full width) ─────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {claimant.firstName} {claimant.lastName}
          </h1>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tortColor(claimant.tortCode)}`}>
            {claimant.tortCode}
          </span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stageColor(claimant.pipelineStage)}`}>
            {claimant.pipelineStage}
          </span>
        </div>
        <p className="text-xs text-gray-400">ID: {claimant.id}</p>
      </div>

      {/* ── TWO-COLUMN BODY ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ════ LEFT COLUMN ════ */}
        <div className="space-y-6">

          {/* 1. Personal Information */}
          <Section title="Personal Information">
            <Field label="Date of Birth">{fmtDate(claimant.dob)}</Field>
            <Field label="SSN (last 4)">{maskedSsn}</Field>
            <Field label="Phone">{claimant.phonePrimary}</Field>
            <Field label="Email">{claimant.email}</Field>
            <Field label="State">{claimant.state}</Field>
            <Field label="Residency Start">{fmtDate(claimant.residencyStartDate)}</Field>
          </Section>

          {/* 2. Address */}
          <Section title="Address">
            <Field label="Raw Address">{claimant.addressRaw}</Field>
            {claimant.addressValidated && (
              <>
                <Field label="Delivery Line">
                  {claimant.addressValidated.deliveryLine1}
                </Field>
                <Field label="Last Line">
                  {claimant.addressValidated.lastLine}
                </Field>
                <Field label="DPV Status">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${dpvColor(claimant.addressValidated.dpvMatchCode)}`}>
                    {dpvLabel(claimant.addressValidated.dpvMatchCode)}&nbsp;({claimant.addressValidated.dpvMatchCode})
                  </span>
                </Field>
              </>
            )}
          </Section>

          {/* 3. Claim Details */}
          <Section title="Claim Details">
            <Field label="Tort Code">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tortColor(claimant.tortCode)}`}>
                {claimant.tortCode}
              </span>
            </Field>
            <Field label="Source Firm">{claimant.sourceFirmId}</Field>
            <Field label="Est. Portfolio Value">{claimant.estPortfolioValueRange}</Field>
            <Field label="Overlap Priority Tier">T{claimant.overlapPriorityTier}</Field>
            <Field label="Cross-Litigation">
              {claimant.crossLitigationFlag
                ? (
                  <span className="inline-flex items-center gap-1 text-amber-600 text-sm">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Flagged
                  </span>
                )
                : <span className="text-gray-400">None</span>
              }
            </Field>
            {overlapIds.length > 0 && (
              <Field label="Match IDs">
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {overlapIds.map(matchId => (
                    // Opens matched claimant detail in a new tab
                    <Link
                      key={matchId}
                      to={`/claimants/${matchId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-blue-700 hover:bg-gray-200 border border-gray-200 transition-colors"
                    >
                      {matchId}
                    </Link>
                  ))}
                </div>
              </Field>
            )}
          </Section>

          {/* 4. Social & Gaming Profile */}
          <Section title="Social & Gaming Profile">
            <Field label="Platforms">
              <div className="flex flex-wrap gap-1 mt-0.5">
                {(claimant.socialMediaPlatforms ?? []).map(p => (
                  <span
                    key={p}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </Field>
            <Field label="Gaming Hours / Day">
              {claimant.gamingHoursPerDay} hrs
            </Field>
          </Section>

        </div>

        {/* ════ RIGHT COLUMN ════ */}
        <div className="space-y-6">

          {/* 5. Notes */}
          <Section title="Notes">
            <p className="text-sm text-gray-400 italic">No notes yet.</p>
            {/* TODO: wire "+ Add Note" to POST /claimants/:id/notes — persist text, author, timestamp server-side */}
            <StubButton label="+ Add Note" title="TODO: POST /claimants/:id/notes" />
          </Section>

          {/* 6. Custom Fields */}
          <Section title="Custom Fields">
            <p className="text-sm text-gray-400 italic">No custom fields configured.</p>
            {/* TODO: wire "+ Add Field" to firm-level custom field schema API — render dynamic key/value pairs per firm config */}
            <StubButton label="+ Add Field" title="TODO: firm custom field schema API" />
          </Section>

          {/* 7. Documents */}
          <Section title="Documents">
            <p className="text-sm text-gray-400 italic">No documents uploaded.</p>
            {/* TODO: wire "+ Upload Document" to POST /claimants/:id/documents — handle S3 multipart upload with presigned URL flow */}
            <StubButton label="+ Upload Document" title="TODO: POST /claimants/:id/documents (S3 multipart)" />
          </Section>

          {/* 8. Metadata */}
          <Section title="Metadata">
            <Field label="Created">{fmtDateTime(claimant.createdAt)}</Field>
            <Field label="Last Updated">{fmtDateTime(claimant.updatedAt)}</Field>
          </Section>

        </div>
      </div>
    </div>
  );
}